/* eslint-disable no-nested-ternary */

import { FaCircle, FaPlus } from 'react-icons/fa';

import { CardData } from '../types/CardData';
import { GameFormat } from '../types/GameFormat';
import { Hole } from '../types/Hole';
import { Tournament } from '../types/Tournament';
import {
  calculatePops,
  formatToParScore,
  getHoleNumberForCourse,
  isStablefordFormat,
  scoreToParColor,
} from '../utils/utils';

export default function LeaderboardDetails({
  event,
  team,
  onHoleClick,
  isGrossOnly = false,
  startAtHandicap = false,
}: {
  event: Tournament;
  team: CardData;
  onHoleClick: (hole: number) => void;
  isGrossOnly?: boolean;
  startAtHandicap?: boolean;
}) {
  function getHoleNumber(hole: number) {
    return team.courseType === 'Back' ? hole + 1 + 9 : hole + 1;
  }

  function holeHeaders(startIndex: number, endIndex: number) {
    const headers = [];
    for (let i = startIndex; i < endIndex; i++) {
      let startingHole = '';
      if (getHoleNumber(i) === team.startingHole) {
        startingHole = 'bg-primary text-white';
      }
      headers.push(
        <th
          key={i}
          className={`w-6 text-xs font-semibold text-center tracking-normal md:w-10 sm:w-6 ${startingHole}`}
          onClick={() => onHoleClick(getHoleNumber(i))}
        >
          {getHoleNumberForCourse(event.selectedCourse, getHoleNumber(i))}
        </th>
      );
    }
    return headers;
  }

  function legendItem(text: string, color: string) {
    return (
      <div className="flex mr-4">
        <div className={`w-4 h-4 mr-1 bg-opacity-25 ${color}`}></div>
        {text}
      </div>
    );
  }

  function calculateHoleParScore(par: number) {
    const holes = team.scores.filter((hole: Hole) => {
      return hole.par === par;
    });
    let scoreToPar = 0;
    holes.forEach((hole: Hole) => {
      if (hole.grossScore !== null) {
        scoreToPar += hole.grossScore - hole.par;
      }
    });
    return (
      <div
        className={`font-bold ${
          scoreToPar < 0
            ? 'text-red-500'
            : scoreToPar === 0
            ? 'text-green-500'
            : 'text-gray-900'
        }`}
      >
        {formatToParScore(scoreToPar)}
      </div>
    );
  }

  function dots(hole: Hole) {
    if (isGrossOnly) {
      return null;
    }

    let pops = calculatePops(team, hole.number ?? 1);

    if (event.format === GameFormat.ThreesFoursFives && hole.par === 5) {
      pops = 0;
    }

    const dotsArr = [];

    for (let i = 0; i < pops; i++) {
      dotsArr.push(
        <FaCircle key={i} className="float-right mr-[2px] text-[4px]" />
      );
    }

    if (pops < 0) {
      dotsArr.push(<FaPlus className="float-right mr-[2px] text-xxxs" />);
    }
    return <div className="h-2 absolute top-[2px] right-0">{dotsArr}</div>;
  }

  function nineHoleTable(startIndex: number, endIndex: number) {
    const is18Holes = team.scores.length > 9 && endIndex === 18;
    const currentScores = team.scores.slice(startIndex, endIndex);
    let parTotal = 0;
    let grossTotal = 0;
    let netTotal = 0;
    let grossPointsTotal = 0;
    let netPointsTotal = 0;

    const overallGrossScore = team.scores.reduce((acc, hole) => {
      return acc + (hole.grossScore as number);
    }, 0);

    const overallNetScore = team.scores.reduce((acc, hole) => {
      return acc + (team.totals?.netScores[hole.number as number] ?? 0);
    }, 0);

    const overallPar = team.scores.reduce((acc, hole) => {
      return acc + hole.par;
    }, 0);

    const overallGrossPoints = team.scores.reduce((acc, hole) => {
      return acc + (team.totals?.grossPoints[hole.number as number] ?? 0);
    }, 0);

    const overallNetPoints = team.scores.reduce((acc, hole) => {
      return acc + (team.totals?.netPoints[hole.number as number] ?? 0);
    }, 0);
    return (
      <table>
        <thead>
          <tr className="border">
            <th className="p-1 text-xs font-semibold text-center tracking-normal"></th>
            {holeHeaders(startIndex, endIndex)}
            <th className="p-1 text-xs font-semibold text-center tracking-normal">
              {team.scores[startIndex]?.number === 1 ? 'Out' : 'In'}
            </th>
            {is18Holes && (
              <th className="p-1 text-xs font-semibold text-center tracking-normal">
                Tot
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          <tr className="border">
            <td className="p-1 text-xs text-center tracking-normal">Par</td>
            {currentScores.map((hole, i) => {
              parTotal += hole.par;
              return (
                <td
                  key={i}
                  className="w-6 text-xs text-center tracking-normal md:w-10 sm:w-6"
                >
                  {hole.par}
                </td>
              );
            })}
            <td className="p-1 text-xs text-center tracking-normal">
              {parTotal}
            </td>
            {is18Holes && (
              <td className="p-1 text-xs text-center tracking-normal">
                {overallPar}
              </td>
            )}
          </tr>
          <tr className="border">
            <td className="p-1 text-xs text-center tracking-normal">
              {isGrossOnly ? 'Score' : 'Gross'}
            </td>
            {currentScores.map((hole, i) => {
              grossTotal += hole.grossScore as number;
              return (
                <td
                  key={i}
                  className={`border relative w-4 text-xs text-center tracking-normal md:w-10 sm:w-6 ${
                    hole.taken ? 'font-extrabold' : ''
                  } ${scoreToParColor(hole.par, hole.grossScore as number)} `}
                >
                  {dots(hole)}
                  {hole.grossScore}
                </td>
              );
            })}
            <td className="p-1 text-xs text-center tracking-normal border">
              {grossTotal}
            </td>
            {is18Holes && (
              <td className="p-1 text-xs text-center tracking-normal border">
                {overallGrossScore}
              </td>
            )}
          </tr>
          {!isGrossOnly && (
            <tr className="border">
              <td className="p-1 text-xs text-center tracking-normal">Net</td>
              {currentScores.map((hole, i) => {
                const netScore = team.totals?.netScores[
                  hole.number as number
                ] as number;
                netTotal += netScore ?? 0;
                return (
                  <td
                    key={i}
                    className={`border w-4 text-xs text-center tracking-normal md:w-10 sm:w-6 ${
                      hole.taken ? 'font-extrabold' : ''
                    } ${scoreToParColor(hole.par, netScore)}`}
                  >
                    {netScore}
                  </td>
                );
              })}
              <td className="p-1 text-xs text-center tracking-normal border">
                {netTotal}
              </td>
              {is18Holes && (
                <td className="p-1 text-xs text-center tracking-normal border">
                  {overallNetScore}
                </td>
              )}
            </tr>
          )}
          {isStablefordFormat(event.format) ? (
            <>
              <tr className="border">
                <td className="p-1 text-xs text-center tracking-normal">
                  Gross Pts
                </td>
                {currentScores.map((hole, i) => {
                  grossPointsTotal +=
                    hole.grossScore !== null
                      ? (team.totals?.grossPoints[
                          hole.number as number
                        ] as number)
                      : 0;
                  return (
                    <td
                      key={i}
                      className={`w-4 text-xs text-center tracking-normal border md:w-10 sm:w-6 ${
                        hole.taken ? 'font-extrabold' : ''
                      }`}
                    >
                      {hole.grossScore !== null
                        ? team.totals?.grossPoints[hole.number as number]
                        : null}
                    </td>
                  );
                })}
                <td className="p-1 text-xs text-center tracking-normal border">
                  {grossPointsTotal}
                </td>
                {is18Holes && (
                  <td className="p-1 text-xs text-center tracking-normal border">
                    {overallGrossPoints}
                  </td>
                )}
              </tr>
              {!isGrossOnly && (
                <tr className="border">
                  <td className="p-1 text-xs text-center tracking-normal">
                    Net Pts
                  </td>
                  {currentScores.map((hole, i) => {
                    netPointsTotal +=
                      hole.grossScore !== null
                        ? (team.totals?.netPoints[
                            hole.number as number
                          ] as number)
                        : 0;
                    return (
                      <td
                        key={i}
                        className={`w-4 text-xs text-center tracking-normal border md:w-10 sm:w-6 ${
                          hole.taken ? 'font-extrabold' : ''
                        }`}
                      >
                        {hole.grossScore !== null
                          ? team.totals?.netPoints[hole.number as number]
                          : null}
                      </td>
                    );
                  })}
                  <td className="p-1 text-xs text-center tracking-normal border">
                    {netPointsTotal}
                  </td>
                  {is18Holes && (
                    <td className="p-1 text-xs text-center tracking-normal border">
                      {overallNetPoints}
                    </td>
                  )}
                </tr>
              )}
            </>
          ) : null}
        </tbody>
      </table>
    );
  }

  return (
    <div className="flex justify-center items-center flex-col">
      {!isGrossOnly && (
        <div className="text-xs tracking-tighter text-gray-700">
          Handicap:{' '}
          {team.handicap >= 0 ? team.handicap : `+${team.handicap * -1}`}
        </div>
      )}
      <div className="text-xs tracking-tighter text-gray-700">
        {startAtHandicap && isStablefordFormat(event.format)
          ? `Quota: ${team.scores.length * 2 - team.handicap}`
          : null}
      </div>

      <div className="flex flex-col xl:flex-row">
        {nineHoleTable(0, 9)}
        {team.scores.length > 9 ? nineHoleTable(9, team.scores.length) : null}
      </div>

      {event.format === GameFormat.Tombstone && team.tombstone != null && (
        <div>
          <div className="text-sm font-semibold text-center tracking-normal">
            Tombstone
          </div>
          <div className="text-sm text-center tracking-normal">
            {team.tombstone}
          </div>
        </div>
      )}

      <div className="flex text-xs mt-4">
        {legendItem('Eagle+', 'bg-yellow-500')}
        {legendItem('Birdie', 'bg-green-500')}
        {legendItem('Bogey', 'bg-red-500')}
        {legendItem('Double Bogey+', 'bg-blue-500')}
      </div>
      <div className="flex border shadow-lg rounded-md my-2 text-gray-700 text-sm md:my-4 md:text-lg">
        <div className="p-2 md:p-4 text-center">
          <div>Par 3s</div>
          {calculateHoleParScore(3)}
        </div>
        <div className="p-2 md:p-4 text-center">
          <div>Par 4s</div>
          {calculateHoleParScore(4)}
        </div>
        <div className="p-2 md:p-4 text-center">
          <div>Par 5s</div>
          {calculateHoleParScore(5)}
        </div>
      </div>
    </div>
  );
}
